/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { Dataset, EventHubsLinkedService, IAttribute, LinkedService, Metamodel, Named, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { IMapperTable, IEventHubsMapperEntity, SchemaLoadStatus, IMapperConnectionInfo } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { escapeDelimiter, getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { EventHubsDataset } from 'app/model/dataset/event-hubs.dataset';
import { EventHubsSettings } from 'app/model/dataflow/dataset-settings/event-hubs-settings';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { Subject, first, lastValueFrom } from 'rxjs';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';
import { DataFlowExpression, createDataflowExpression } from 'app/model/dataflow/dataflowexpression';

export class EventHubsDatasetTypeHandler extends DatasetTypeHandler {

    public supportedFormats = ['json', 'avro', 'delimited'];

    constructor(
        public isSource: Boolean
    ) {
        super();
    }

    public getOkButtonStatus(selectedRows: IEventHubsMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IEventHubsMapperEntity[];
            rows.forEach((elem: IEventHubsMapperEntity) => {
                disableButton = !(elem.eventHubsName?.length > 0 || elem.eventHubsNamePattern?.length > 0) ||
                (elem.schemaInferenceType === DslConnectorConstants.payloadSchema && !(elem.payloadSchema?.length > 0) ||
                (elem.schemaInferenceType === DslConnectorConstants.autoInferSchema && (elem.numSampleRecords === undefined || elem.numSampleRecords < 1)));
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !this._areAllPathsAreUnique(rows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [true, ''];
    }

    public processSelectedRows(selectedRows: IEventHubsMapperEntity[], selectedSourceLinkedService: LinkedService, selectedDatasetFormat: DatasetFormatType): IMapperTable[] {
        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dslConnectorProperties: Map<string, any> = new Map([
                [DslConnectorConstants.format, elem.format]
            ]);
            //eventHubsNamePattern and eventHubsName are mutually exclusive
            if (elem.eventHubsNamePattern) {
                dslConnectorProperties.set(DslConnectorConstants.eventHubsNamePattern, elem.eventHubsNamePattern);
            } else {
                dslConnectorProperties.set(DslConnectorConstants.eventHubsName, elem.eventHubsName);
            }
            if (elem.format !== DslConnectorConstants.passThrough) {
                dslConnectorProperties.set(DslConnectorConstants.schemaInferenceType, elem.schemaInferenceType);
            }
            if (elem.schemaInferenceType === DslConnectorConstants.payloadSchema) {
                dslConnectorProperties.set(DslConnectorConstants.payloadSchema, elem.payloadSchema);
            }
            if (elem.schemaInferenceType === DslConnectorConstants.autoInferSchema) {
                dslConnectorProperties.set(DslConnectorConstants.numSampleRecords, elem.numSampleRecords);
            }
            dslConnectorProperties.set(DslConnectorConstants.includeHeaders, elem.includeHeaders);

            tables.push({
                name: getValidTransformationName(this.isSource, elem.entityName),
                // dataset doesn't exist in UX for now. Will use work-around at places required.
                sourceDataset: undefined,
                sourceLinkedService: selectedSourceLinkedService,
                isInlineDataset: true,
                datasetSettings: undefined,
                wildcardPaths: [],
                dslConnectorProperties,
                datasetType: selectedDatasetFormat,
                schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
                tableName: elem.entityName
            });
        });

        return tables;
    }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        const eventHubsSettings = Owned.create<EventHubsSettings>(EventHubsSettings.MODEL, outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as EventHubsSettings;
        eventHubsSettings.initialize('');
        eventHubsSettings.eventHubsName.expression = !incomingSourceDatasetsSettings.eventHubsName.expression ? undefined : incomingSourceDatasetsSettings.eventHubsName.expression;
        eventHubsSettings.eventHubsNamePattern.expression = !incomingSourceDatasetsSettings.eventHubsNamePattern.expression ? undefined : incomingSourceDatasetsSettings.eventHubsNamePattern.expression;
        eventHubsSettings.format.expression = incomingSourceDatasetsSettings.format.expression;
        eventHubsSettings.payloadSchema.expression = !incomingSourceDatasetsSettings.payloadSchema.expression ? undefined : incomingSourceDatasetsSettings.payloadSchema.expression;
        eventHubsSettings.isAutoInfer.expression = !incomingSourceDatasetsSettings.isAutoInfer.expression ? undefined : incomingSourceDatasetsSettings.isAutoInfer.expression;
        eventHubsSettings.includeHeaders.expression = incomingSourceDatasetsSettings.includeHeaders.expression;
        eventHubsSettings.numSampleRecords.expression = !incomingSourceDatasetsSettings.numSampleRecords.expression ? undefined : incomingSourceDatasetsSettings.numSampleRecords.expression;
        eventHubsSettings.instanceSelectionType.expression = eventHubsSettings.eventHubsName.expression ? stringResources.InstanceSelectionType_Manual : stringResources.InstanceSelectionType_Pattern;
        if (!this.isSource) {
            eventHubsSettings.isSinkSchemaQuery = true;
        }
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = eventHubsSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        const eventHubsSettings = Owned.create<EventHubsSettings>(EventHubsSettings.MODEL, table as DataFlowSource);
        eventHubsSettings.initialize('');
        eventHubsSettings.eventHubsName.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.eventHubsName);
        eventHubsSettings.eventHubsNamePattern.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.eventHubsNamePattern);
        eventHubsSettings.format.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.format);
        eventHubsSettings.payloadSchema.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.payloadSchema);
        eventHubsSettings.isAutoInfer = createDataflowExpression(getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaInferenceType) === DslConnectorConstants.autoInferSchema ? 'true' : 'false') as DataFlowExpression;
        eventHubsSettings.includeHeaders.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.includeHeaders);
        eventHubsSettings.numSampleRecords.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.numSampleRecords);
        eventHubsSettings.instanceSelectionType.expression = eventHubsSettings.eventHubsName.expression ? stringResources.InstanceSelectionType_Manual : stringResources.InstanceSelectionType_Pattern;
        return eventHubsSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const dataset = new EventHubsDataset(Metamodel.modelFor(EventHubsDataset).name);
        dataset.linkedService = linkedService;
        return dataset;
    }

    public updateMapperTablePropertiesOnEdit(existingMapperTable: IMapperTable, updatedMapperTable: IMapperTable): void {
        existingMapperTable.datasetSettings = updatedMapperTable.datasetSettings;
        existingMapperTable.dslConnectorProperties = updatedMapperTable.dslConnectorProperties;
    }

    public doReImportSchemaforEditOption(existingMapperTable: IMapperTable, updatedMapperTable: IMapperTable): boolean {
        if (!!existingMapperTable) {
            return existingMapperTable.dslConnectorProperties.get('eventHubName') !== updatedMapperTable.dslConnectorProperties.get('eventHubName')
                || existingMapperTable.dslConnectorProperties.get('eventHubNamePattern') !== updatedMapperTable.dslConnectorProperties.get('eventHubNamePattern')
                || existingMapperTable.dslConnectorProperties.get('format') !== updatedMapperTable.dslConnectorProperties.get('format')
                || existingMapperTable.dslConnectorProperties.get('payloadSchema') !== updatedMapperTable.dslConnectorProperties.get('payloadSchema')
                || existingMapperTable.dslConnectorProperties.get('includeHeaders') !== updatedMapperTable.dslConnectorProperties.get('includeHeaders')
                || existingMapperTable.dslConnectorProperties.get('numSampleRecords') !== updatedMapperTable.dslConnectorProperties.get('numSampleRecords');
        }
        return true;
    }

    public async fetchEntitiesUsingDebugApi(queryApiFactoryService: QueryApiFactoryService, linkedService: LinkedService, isSource: boolean,
        getEntitiesForADataFlowSource: (source: DataFlowSource, resultSubject: Subject<any>) => Promise<void> = undefined): Promise<any[]> {
            const dataMappingModel: AdfDataMapper= new AdfDataMapper('datamapper');
            const dataflow: DataFlow = Named.create<DataFlow>(DataFlow.MODEL, dataMappingModel);
            const source: DataFlowSource = Named.create<DataFlowSource>(DataFlowSource.MODEL, dataflow);
            source.sourceLinkedService = linkedService;
            const resultSubject = new Subject<IEventHubsMapperEntity[]>();
            const firstResult = lastValueFrom(resultSubject.pipe(first()));
            const eventHubsSettings = Owned.create<EventHubsSettings>(EventHubsSettings.MODEL, source);
            eventHubsSettings.initialize(undefined);
            eventHubsSettings.format.expression = 'passthrough';
            eventHubsSettings.store = 'eventhubs';
            eventHubsSettings.authenticationType = 'SASL';
            eventHubsSettings.saslMechanism = 'PLAIN';
            eventHubsSettings.securityProtocol = 'SASL_SSL';
            eventHubsSettings.accessKey = this.valueToResource(linkedService, EventHubsLinkedService.ACCESS_KEY);
            eventHubsSettings.accessKeyName = this.valueToResource(linkedService, EventHubsLinkedService.ACCESS_KEY_NAME);
            eventHubsSettings.namespaceName = this.valueToResource(linkedService,EventHubsLinkedService.NAMESPACE_NAME);
            source.datasetSettings = eventHubsSettings;
            source.isInlineDataset = true;
            source.name = 'temp';
            dataflow.addSource(source);
            dataflow.name = 'temp';
            source.addOutputStream();
            await getEntitiesForADataFlowSource(source, resultSubject);
            return await firstResult;
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        const commonDslConnectorProperties = mapperConnectionInfo.connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.store, 'eventhubs');
        const format = mapperConnectionInfo.tables[0].dslConnectorProperties.get('format');
        if (format === 'delimited') {
            commonDslConnectorProperties.set(DslConnectorConstants.columnNamesAsHeader, true);
            commonDslConnectorProperties.set(DslConnectorConstants.columnDelimiter, escapeDelimiter(','));
            commonDslConnectorProperties.set(DslConnectorConstants.quoteChar, escapeDelimiter('\\\"'));
            commonDslConnectorProperties.set(DslConnectorConstants.escapeChar, escapeDelimiter('\\\\'));
            commonDslConnectorProperties.set(DslConnectorConstants.rowDelimiter, escapeDelimiter('\\n'));
            commonDslConnectorProperties.set(DslConnectorConstants.fileSystem, 'eventhubs');
        }
        if (this.isSource) {
            commonDslConnectorProperties.set(DslConnectorConstants.enableCdc, true);
            commonDslConnectorProperties.set(DslConnectorConstants.skipInitialLoad, true);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected valueToResource(object: {}, attribute: IAttribute): any {
        const value = attribute.templateValue(object);
        return value !== undefined ? value : attribute.value(object);
    }

    private _areAllPathsAreUnique(filePaths: IEventHubsMapperEntity[]): boolean {
        const seenPaths = new Set<string>();
        for (const path of filePaths) {
            if (seenPaths.has(path.eventHubsName)) {
                return false;
            }
            seenPaths.add(path.eventHubsName);
        }
        return true;
    }
}
